import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Plus, Check, Building2 } from 'lucide-react';
import apiClient from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function BusinessSwitcher({ onSwitch }) {
  const [businesses, setBusinesses] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    loadBusinesses();
  }, []);

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setCreating(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadBusinesses = async () => {
    try {
      const res = await apiClient.get('/api/businesses');
      setBusinesses(res.data.businesses || []);
      const stored = sessionStorage.getItem('active_business_id');
      setCurrentId(stored || res.data.current_business_id);
    } catch (err) {
      console.error('[BusinessSwitcher] Failed to load:', err);
    }
  };

  const switchTo = (id) => {
    sessionStorage.setItem('active_business_id', id);
    setCurrentId(id);
    setOpen(false);
    if (onSwitch) onSwitch(id);
    window.location.reload();
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      const res = await apiClient.post('/api/businesses', { name: newName.trim() });
      const biz = res.data.business;
      setBusinesses((prev) => [...prev, biz]);
      setNewName('');
      setCreating(false);
      switchTo(biz.id);
    } catch (err) {
      console.error('[BusinessSwitcher] Create failed:', err);
    }
  };

  if (businesses.length <= 1 && !open) {
    const current = businesses[0];
    if (!current) return null;
    return (
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm text-[#9CB0C7] hover:text-white hover:bg-[#1E3A5F]/50 transition-all max-w-[180px]"
      >
        <Building2 className="w-4 h-4 flex-shrink-0 text-[#3EF1C7]" />
        <span className="truncate">{current.name}</span>
      </button>
    );
  }

  const current = businesses.find((b) => b.id === currentId) || businesses[0];

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => { setOpen(!open); setCreating(false); }}
        className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm text-[#9CB0C7] hover:text-white hover:bg-[#1E3A5F]/50 transition-all max-w-[200px]"
        data-testid="business-switcher-button"
      >
        <Building2 className="w-4 h-4 flex-shrink-0 text-[#3EF1C7]" />
        <span className="truncate font-medium text-white">{current?.name || 'Select Business'}</span>
        <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 w-72 bg-[#0E1426] border border-[#1E3A5F] rounded-lg shadow-2xl overflow-hidden z-50">
          <div className="p-2 border-b border-[#1E3A5F]">
            <p className="text-[10px] uppercase tracking-wider text-[#6B7F95] px-2 py-1 font-semibold">Your Businesses</p>
          </div>
          <div className="p-1 max-h-60 overflow-y-auto">
            {businesses.map((biz) => (
              <button
                key={biz.id}
                onClick={() => switchTo(biz.id)}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-md text-sm transition-colors text-left ${
                  biz.id === currentId
                    ? 'bg-[#1E3A5F] text-white'
                    : 'text-[#9CB0C7] hover:text-white hover:bg-[#1E3A5F]/50'
                }`}
                data-testid={`business-option-${biz.id}`}
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-7 h-7 rounded-md bg-gradient-to-br from-[#3EF1C7]/20 to-[#7A5CFF]/20 border border-[#1E3A5F] flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-[#3EF1C7]">
                      {biz.name?.charAt(0)?.toUpperCase() || 'B'}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium truncate">{biz.name}</p>
                    <p className="text-[10px] text-[#6B7F95] truncate">{biz.subscription_plan} {biz.role !== 'owner' ? `(${biz.role})` : ''}</p>
                  </div>
                </div>
                {biz.id === currentId && <Check className="w-4 h-4 text-[#3EF1C7] flex-shrink-0" />}
              </button>
            ))}
          </div>

          <div className="p-2 border-t border-[#1E3A5F]">
            {creating ? (
              <form onSubmit={handleCreate} className="flex gap-2">
                <input
                  autoFocus
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Business name..."
                  className="flex-1 px-3 py-2 rounded-md bg-[#0A1628] border border-[#1E3A5F] text-white text-sm placeholder-[#6B7F95] focus:outline-none focus:border-[#3EF1C7]"
                />
                <button
                  type="submit"
                  className="px-3 py-2 rounded-md bg-[#3EF1C7] text-[#0A1628] text-sm font-semibold hover:bg-[#3EF1C7]/90 transition-colors"
                >
                  Add
                </button>
              </form>
            ) : (
              <button
                onClick={() => setCreating(true)}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-[#3EF1C7] hover:bg-[#1E3A5F]/50 transition-colors"
                data-testid="add-business-button"
              >
                <Plus className="w-4 h-4" />
                <span>Add Business</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
